/**
 * ProfileWorkload
 *
 * Shows workload statistics and heat map for a user's tasks.
 */

import {
  CheckCircle2,
  CircleDot,
  AlertCircle,
  Clock,
  AlertTriangle,
  Target,
  Circle,
} from 'lucide-react';
import clsx from 'clsx';
import { formatDuration } from '@/utils/formatters';
import { WorkloadHeatMap } from './WorkloadHeatMap';
import type { WorkloadStats } from '@/hooks/useProfile';
import type { Task } from '@/db';

interface ProfileWorkloadProps {
  stats: WorkloadStats;
  tasks: (Task & { id: string })[];
}

export function ProfileWorkload({ stats, tasks }: ProfileWorkloadProps) {
  return (
    <div className="space-y-4 animate-fade-in">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          icon={<Target className="w-4 h-4" />}
          label="Task totali"
          value={stats.totalTasks}
          color="violet"
        />
        <StatCard
          icon={<CheckCircle2 className="w-4 h-4" />}
          label="Completati"
          value={stats.completedTasks}
          color="emerald"
          subtitle={`${stats.completionRate}%`}
        />
        <StatCard
          icon={<CircleDot className="w-4 h-4" />}
          label="In corso"
          value={stats.inProgressTasks}
          color="blue"
        />
        <StatCard
          icon={<AlertTriangle className="w-4 h-4" />}
          label="Scaduti"
          value={stats.overdueTasks}
          color="rose"
        />
      </div>

      {/* Completion Progress */}
      <div className="bg-surface-secondary/30 rounded-xl border border-border-primary p-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-[13px] font-semibold text-text-primary">Progresso completamento</h4>
          <span className="text-[12px] font-semibold text-accent-violet tabular-nums">
            {stats.completionRate}%
          </span>
        </div>
        <div className="h-2 bg-surface-tertiary rounded-full overflow-hidden">
          <div
            className="h-full bg-accent-emerald rounded-full transition-all duration-500"
            style={{ width: `${stats.completionRate}%` }}
          />
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-[10px] text-text-quaternary">
            {stats.completedTasks} di {stats.totalTasks} task completati
          </span>
          <div className="flex items-center gap-3">
            <StatusPill
              icon={<Circle className="w-2.5 h-2.5" />}
              count={stats.openTasks}
              label="Aperti"
              color="text-text-quaternary"
            />
            <StatusPill
              icon={<CircleDot className="w-2.5 h-2.5" />}
              count={stats.inProgressTasks}
              label="In corso"
              color="text-accent-blue"
            />
            <StatusPill
              icon={<AlertCircle className="w-2.5 h-2.5" />}
              count={stats.blockedTasks}
              label="Bloccati"
              color="text-accent-rose"
            />
          </div>
        </div>
      </div>

      {/* Time Stats */}
      {stats.totalTimeEstimated > 0 && (
        <div className="bg-surface-secondary/30 rounded-xl border border-border-primary p-4">
          <h4 className="text-[13px] font-semibold text-text-primary mb-3">Tempo</h4>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-3.5 h-3.5 text-text-quaternary" />
              <span className="text-[10px] text-text-quaternary uppercase tracking-wider font-medium">
                Stimato
              </span>
            </div>
            <p className="text-[14px] font-semibold text-text-primary tabular-nums">
              {formatDuration(stats.totalTimeEstimated)}
            </p>
          </div>
        </div>
      )}

      {/* Heat Map */}
      <WorkloadHeatMap tasks={tasks} days={91} />
    </div>
  );
}

// --- Helper Components ---

function StatCard({
  icon,
  label,
  value,
  color,
  subtitle,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  color: 'violet' | 'emerald' | 'blue' | 'rose';
  subtitle?: string;
}) {
  const colorClasses = {
    violet: 'text-accent-violet bg-accent-violet/10',
    emerald: 'text-accent-emerald bg-accent-emerald/10',
    blue: 'text-accent-blue bg-accent-blue/10',
    rose: 'text-accent-rose bg-accent-rose/10',
  };

  return (
    <div className="bg-surface-secondary/30 rounded-xl border border-border-primary p-3">
      <div className="flex items-center gap-2 mb-2">
        <div
          className={clsx(
            'w-7 h-7 rounded-lg flex items-center justify-center',
            colorClasses[color]
          )}
        >
          {icon}
        </div>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-[18px] font-bold text-text-primary tabular-nums">{value}</span>
        {subtitle && (
          <span className={clsx('text-[11px] font-medium', `text-accent-${color}`)}>{subtitle}</span>
        )}
      </div>
      <p className="text-[10px] text-text-quaternary mt-0.5">{label}</p>
    </div>
  );
}

function StatusPill({
  icon,
  count,
  label,
  color,
}: {
  icon: React.ReactNode;
  count: number;
  label: string;
  color: string;
}) {
  return (
    <div className={clsx('flex items-center gap-1', color)}>
      {icon}
      <span className="text-[10px] font-medium tabular-nums">{count}</span>
      <span className="text-[9px] text-text-quaternary">{label}</span>
    </div>
  );
}
